import React, { useEffect, useState } from 'react';
import { maintenanceAPI, vehicleAPI } from '../api/client';
import { Wrench, Plus, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const Maintenance = () => {
  const [maintenance, setMaintenance] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    if (selectedVehicle) {
      fetchMaintenance();
    }
  }, [selectedVehicle]);

  const fetchVehicles = async () => {
    try {
      const response = await vehicleAPI.list();
      setVehicles(response.data);
      if (response.data.length > 0) {
        setSelectedVehicle(response.data[0].vehicle_id);
      }
    } catch (error) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenance = async () => {
    if (!selectedVehicle) return;
    try {
      const response = await maintenanceAPI.list(selectedVehicle);
      setMaintenance(response.data);
    } catch (error) {
      toast.error('Failed to load maintenance records');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const formData = new FormData(e.target);
    const data = {
      vehicle_id: selectedVehicle,
      date: formData.get('date'),
      type: formData.get('type'),
      mileage: parseInt(formData.get('mileage')),
      description: formData.get('description'),
      cost: formData.get('cost') ? parseFloat(formData.get('cost')) : null,
      mechanic_name: formData.get('mechanic_name'),
      mechanic_contact: formData.get('mechanic_contact'),
      parts_replaced: formData.get('parts_replaced'),
      next_service_due: formData.get('next_service_due')
    };

    try {
      await maintenanceAPI.create(data);
      toast.success('Maintenance record added');
      setDialogOpen(false);
      fetchMaintenance();
      e.target.reset();
    } catch (error) {
      toast.error('Failed to add record');
    } finally {
      setSubmitting(false);
    }
  };

  const currentVehicle = vehicles.find(v => v.vehicle_id === selectedVehicle);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">Maintenance History</h1>
          <p className="text-muted-foreground mt-1">Your digital service book</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-maintenance-button" disabled={!selectedVehicle}>
              <Plus className="h-4 w-4 mr-2" />
              Add Service Record
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Maintenance Record</DialogTitle>
              <DialogDescription>Record service or repair work</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input name="date" type="date" required defaultValue={format(new Date(), 'yyyy-MM-dd')} />
                </div>
                <div>
                  <Label>Mileage *</Label>
                  <Input name="mileage" type="number" required placeholder="12345" />
                </div>
              </div>
              <div>
                <Label>Type *</Label>
                <Select name="type" required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="mot">MOT Test</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea name="description" required placeholder="What work was done?" rows={3} />
              </div>
              <div>
                <Label>Cost (£)</Label>
                <Input name="cost" type="number" step="0.01" placeholder="125.00" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Mechanic/Garage Name</Label>
                  <Input name="mechanic_name" placeholder="ABC Motors" />
                </div>
                <div>
                  <Label>Mechanic Contact</Label>
                  <Input name="mechanic_contact" placeholder="Phone or email" />
                </div>
              </div>
              <div>
                <Label>Parts Replaced</Label>
                <Input name="parts_replaced" placeholder="Oil filter, brake pads" />
              </div>
              <div>
                <Label>Next Service Due (date)</Label>
                <Input name="next_service_due" type="date" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? 'Saving...' : 'Add Record'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Vehicle Selector */}
      {vehicles.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label>Select Vehicle</Label>
                <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                  <SelectTrigger className="max-w-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map(v => (
                      <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                        {v.year} {v.make} {v.model} ({v.reg_plate})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {maintenance.length > 0 && (
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export History
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      {maintenance.length > 0 && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Total Services</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold">{maintenance.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold">
                £{maintenance.reduce((sum, m) => sum + (m.cost || 0), 0).toFixed(2)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Last Service</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xl font-semibold">
                {maintenance[0] ? format(parseISO(maintenance[0].date), 'dd MMM yyyy') : 'N/A'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Timeline */}
      {maintenance.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Service History Timeline</CardTitle>
            <CardDescription>Complete maintenance record for {currentVehicle?.reg_plate}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>
              
              <div className="space-y-8">
                {maintenance.map((record, index) => (
                  <div key={record.maintenance_id} className="relative flex gap-6">
                    {/* Timeline dot */}
                    <div className="relative z-10 flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 border-4 border-background">
                      <Wrench className="h-5 w-5 text-primary" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pb-8">
                      <div className="bg-muted/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-semibold text-lg capitalize">{record.type}</p>
                            <p className="text-sm text-muted-foreground">
                              {format(parseISO(record.date), 'dd MMMM yyyy')} • {record.mileage.toLocaleString()} miles
                            </p>
                          </div>
                          {record.cost && (
                            <p className="text-lg font-semibold">£{record.cost.toFixed(2)}</p>
                          )}
                        </div>
                        <p className="text-sm mb-3">{record.description}</p>
                        {record.parts_replaced && (
                          <p className="text-sm text-muted-foreground mb-2">
                            <span className="font-medium">Parts:</span> {record.parts_replaced}
                          </p>
                        )}
                        {record.mechanic_name && (
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium">By:</span> {record.mechanic_name}
                            {record.mechanic_contact && ` (${record.mechanic_contact})`}
                          </p>
                        )}
                        {record.next_service_due && (
                          <p className="text-sm text-primary mt-2">
                            Next service due: {format(parseISO(record.next_service_due), 'dd MMM yyyy')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : selectedVehicle ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Wrench className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">No Maintenance Records</h3>
            <p className="text-muted-foreground mb-6">Start building your digital service book</p>
            <Button onClick={() => setDialogOpen(true)}>Add First Service Record</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <Wrench className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">No Vehicles</h3>
            <p className="text-muted-foreground mb-6">Add a vehicle first to track maintenance</p>
            <Button asChild>
              <a href="/app/vehicles/add">Add Vehicle</a>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Maintenance;
